import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Select, Input, toast, FormWrap, TelephoneInput } from 'day8-ui';
import { countries } from 'countries-list';
import { updateUser } from '../../actions/user';
import history from '../../history';

const days = [...Array(31)].map((day, i) => ({
  text: i + 1,
  value: i + 1
}));

const months = [
  {
    text: 'Jan',
    value: 1
  },
  {
    text: 'Feb',
    value: 2
  },
  {
    text: 'Mar',
    value: 3
  },
  {
    text: 'Apr',
    value: 4
  },
  {
    text: 'May',
    value: 5
  },
  {
    text: 'Jun',
    value: 6
  },
  {
    text: 'Jul',
    value: 7
  },
  {
    text: 'Aug',
    value: 8
  },
  {
    text: 'Sep',
    value: 9
  },
  {
    text: 'Oct',
    value: 10
  },
  {
    text: 'Nov',
    value: 11
  },
  {
    text: 'Dec',
    value: 12
  }
];

function range(start, count) {
  return Array.apply(0, Array(count))
    .map((element, index) => ({
      text: start - index,
      value: start - index
    }));
}

const years = range(2019, 80);

const RequiredUserDetails = props => {

  const [user, setUser] = useState({}); // form data
  const [userResponse, setUserResponse] = useState({}); // initial response to check if user has passport
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState({});
  const countryList = Object.entries(countries)
    .map(([countryCode, countryData]) => ({ value: countryCode, text: countryData.name }))
    .sort((a, b) => (a.text > b.text) ? 1 : -1);

  useEffect(() => {
    fetch('/api/account/profile/edit', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin'
    }).then(res => res.json()).then(response => {
      if (response?.data) {
        setUserResponse(response.data.profile);
        setUser({
          ...response.data.profile,
          birthDate: {
            day: response.data.profile.birthDate && moment.utc(response.data.profile.birthDate).date(),
            month: response.data.profile.birthDate && moment.utc(response.data.profile.birthDate).month() + 1, // this returns values 0-11 -> php accepts 1-12
            year: response.data.profile.birthDate && moment.utc(response.data.profile.birthDate).year()
          },
          _token: response.data._token
        });
      }
    });
  }, []);

  const handleFormChange = e => {
    setUser({
      ...user,
      [e.target.name]: e.target.value,
      [e.target.name]: e.target.dataset?.repeatType ? {
        ...user[e.target.name],
        [e.target.dataset.repeatType]: e.target.value
      } : e.target.value
    });
  };


  const handleFormSubmit = e => {
    e.preventDefault();
    setNotifications({});
    setLoading(true);
    const {
      location, redirectTo, updateUser, onSuccess
    } = props;
    const returnTo = location && location.state && location.state.returnTo;
    const validateForBooking = true;
    updateUser(user, validateForBooking).then(({ status, data }) => {
      setLoading(false);
      if (status === 'success') {
        toast.success("Profile updated.");
        if (onSuccess) {
          return onSuccess();
        }
        if (redirectTo) {
          // Redirect used to force new page reload
          return window.location = (redirectTo);
        }
        // Used for react router
        return returnTo ? history.push(returnTo) : history.push('/account');
      }
      const errors = {};
      Object.keys(data.errors).forEach(error => {
        errors[error] = {
          notificationType: 'error',
          notificationMessage: Array.isArray(data.errors[error]) ? data.errors[error][0] : Object.entries(data.errors[error])[0][1][0], // this is how nested those error messages are coming from Symfony...
          showNotification: true,
          onClose: () => setNotifications({})
        };
      });
      setNotifications({
        error: {
          ...errors
        }
      });
    }).catch(e => console.warn(e));
  };
  const genderOptions = [
    {
      text: 'Male',
      value: 'M'
    },
    {
      text: 'Female',
      value: 'F'
    }
  ];
  return (
    <div>
      <p>It looks like you are missing some details. Please fill in the fields below.</p>
      <FormWrap className='dui-mt-4' onSubmit={handleFormSubmit} loading={loading}>
        {(props.requiredDetails && props.requiredDetails.length > 0 && props.requiredDetails.some(detail => detail.property_path === 'country')) && (
          <Select
            className='dui-w-full'
            containerClassName='dui-w-full dui-mt-4'
            label="COUNTRY*"
            defaultLabel={"Choose..."}
            options={countryList}
            value={user.country || ""}
            onChange={handleFormChange}
            name="country"
            errorMessage={notifications.error && notifications.error.country?.notificationMessage}
            required
          />
        )}
        {(props.requiredDetails && props.requiredDetails.length > 0 && props.requiredDetails.some(detail => detail.property_path === 'lastName')) && (
          <Input
            className='dui-w-full'
            containerClassName='dui-w-full dui-mt-4'
            label="Last Name*"
            type="text"
            name="lastName"
            placeholder="Last name"
            value={user.lastName}
            errorMessage={notifications.error && notifications.error.lastName?.notificationMessage}
            onChange={handleFormChange}
            required
          />
        )}
        {(props.requiredDetails && props.requiredDetails.length > 0 && props.requiredDetails.some(detail => detail.property_path === 'sex')) && (
          <Select
            className='dui-w-full'
            containerClassName='dui-w-full dui-mt-4'
            label="Gender*"
            onChange={e => handleFormChange({ target: { name: 'sex', value: e.target.value } })}
            value={user.sex || ""}
            defaultLabel={"Choose..."}
            options={genderOptions}
            errorMessage={notifications.error && notifications.error.sex?.notificationMessage}
            required
          />
        )}
        {(props.requiredDetails && props.requiredDetails.length > 0 && props.requiredDetails.some(detail => detail.property_path === 'birthDate')) && (
          <Fragment>
            <label className="dui-block dui-mb-2 dui-mt-4">Birth Date*</label>
            <div className='dui-grid dui-grid-cols-3 dui-gap-2'>
              <Select
                className='dui-w-full'
                labelHidden
                defaultLabel={"Day..."}
                options={days}
                name="birthDate"
                dataRepeatType="day"
                value={user.birthDate?.day || ""}
                onChange={handleFormChange}
                errorMessage={!!notifications?.error?.birthDate}
              />
              <Select
                className='dui-w-full'
                labelHidden
                defaultLabel={"Month..."}
                options={months}
                name="birthDate"
                dataRepeatType="month"
                value={user.birthDate?.month || ""}
                onChange={handleFormChange}
                errorMessage={!!notifications?.error?.birthDate}
              />
              <Select
                className='dui-w-full'
                labelHidden
                defaultLabel={"Year..."}
                options={years}
                name="birthDate"
                dataRepeatType="year"
                value={user.birthDate?.year || ""}
                onChange={handleFormChange}
                errorMessage={!!notifications?.error?.birthDate}
              />
            </div>
            {notifications.error && notifications.error.birthDate && (
              <p className='dui-text-red-500 dui-text-sm dui-mt-2'>{notifications.error.birthDate?.notificationMessage}</p>
            )}
          </Fragment>
        )}
        {(props.requiredDetails && props.requiredDetails.length > 0 && props.requiredDetails.some(detail => detail.property_path === 'phone')) && (
          <div className='dui-mt-4'>
            <TelephoneInput
              inputContainerClassName='dui-flex-grow'
              className='dui-w-full'
              inputClassName='dui-w-full'
              onChange={number => handleFormChange({ target: { name: 'phone', value: number } }) }
              defaultCountry={user.country}
              label="Phone number*"
              errorMessage={notifications.error && notifications.error.phone?.notificationMessage}
              value={user.phone}
              required
            />
          </div>
        )}
        {(props.requiredDetails && props.requiredDetails.length > 0 && !userResponse.passport) && (
          <Input
            className='dui-w-full'
            containerClassName='dui-w-full dui-mt-4'
            label="Passport number (optional)"
            type="text"
            name="passport"
            placeholder="Number on your passport"
            value={user.passport}
            errorMessage={notifications.error && notifications.error.passport?.notificationMessage}
            onChange={handleFormChange}
          />
        )}
        <Button className='dui-mt-8 dui-w-full' type="submit" disabled={loading} >{loading ? 'Loading...' : 'Submit'}</Button>
      </FormWrap>
    </div>
  );
};


RequiredUserDetails.propTypes = {
  updateUser: PropTypes.func.isRequired,
  redirectTo: PropTypes.string,
  location: PropTypes.object,
  requiredDetails: PropTypes.array,
  onSuccess: PropTypes.func
};

const mapStateToProps = store => ({
  user: store.user.loggedUser,
  requiredDetails: store.user.requiredDetails,
  onSuccess: store.user.onSuccess
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateUser
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RequiredUserDetails);
